import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, RotateCcw, Pencil, Check } from 'lucide-react';
import { PROF, PROF_WE, profOf } from '../lib/aquaNet';
import { profileFromFile } from '../lib/profImport';

const nf = (v, d = 2) => (+v).toFixed(d).replace('.', ',');

function ProfChart({ own, std }) {
  const W = 288, H = 70, mx = Math.max(...own, ...std) * 1.1, bw = W / 24;
  return (
    <svg className="dprof-chart" viewBox={`0 0 ${W} ${H + 12}`} data-testid="pf-chart">
      {own.map((v, h) => <rect key={h} x={h * bw + 1} y={H - (v / mx) * H} width={bw - 2} height={(v / mx) * H} fill="#0ea5e9" />)}
      <polyline points={std.map((v, h) => `${h * bw + bw / 2},${H - (v / mx) * H}`).join(' ')} fill="none" stroke="#f97316" strokeWidth="1.6" strokeDasharray="4 3" />
      {[0, 6, 12, 18].map((h) => <text key={h} x={h * bw + 1} y={H + 10} fontSize="10" fontFamily="Arial, sans-serif" fill="#64748b">{`${h} h`}</text>)}
    </svg>
  );
}

// own day profile of the project from meter data: file import or 24 hourly values; used by night measurement and water age
export function ProfileBox({ t, cfg, setCfg, day = 'wd' }) {
  const we = day === 'we', K = we ? 'profWe' : 'prof', SK = we ? 'profWeSrc' : 'profSrc', std = we ? PROF_WE : PROF;
  const own = Array.isArray(cfg[K]) && cfg[K].length === 24, [edit, setEdit] = useState(false), [vals, setVals] = useState(null), fr = useRef();
  const cur = profOf(cfg, day);
  const open = () => { setVals((own ? cfg[K] : std.map((v) => +v.toFixed(2))).map(String)); setEdit(true); };
  const save = () => { const v = vals.map((x) => parseFloat(String(x).replace(',', '.'))); if (v.some((x) => !Number.isFinite(x) || x < 0) || !v.some((x) => x > 0)) return toast.error(t.dprofBad); setCfg({ [K]: v, [SK]: t.dprofManual }); setEdit(false); };
  const imp = async (f) => {
    if (!f) return;
    try {
      const r = await profileFromFile(f), src = `${f.name} · ${r.n} ${t.dprofRows}${r.days ? ` · ${r.days} ${t.dprofDays}` : ''}${r.cumulative ? ` · ${t.dprofCum}` : ''}`;
      if (r.profWe) { setCfg({ prof: r.prof, profSrc: `${src} · ${t.lfDay_wd}`, profWe: r.profWe, profWeSrc: `${src} · ${t.lfDay_we}`, weF: r.weF }); toast.success(t.dprofOkSplit.replace('{f}', nf(r.weF))); }
      else { setCfg({ [K]: r.prof, [SK]: src }); toast.success(t.dprofOk); }
      setEdit(false);
    } catch (e) { toast.error(t['dprofErr_' + e.message] || t.dprofErr_few); }
    fr.current.value = '';
  };
  return (
    <div className="dprof-box" data-testid="pf-box">
      <div className="aq-row"><b className="aq-sub">{t.dprofTitle}:</b> <span data-testid="pf-state">{own ? t.dprofOwn : we ? t.dprofStdWe : t.dprofStd} · {t['lfDay_' + day]}</span></div>
      {own && cfg[SK] && <small className="muted" data-testid="pf-src">{cfg[SK]}</small>}
      <ProfChart own={cur} std={std} />
      {we && <label className="aq-row">{t.dprofWeF}<input type="number" step="0.05" min="0" data-testid="pf-wef" value={cfg.weF ?? ''} placeholder="1" onChange={(e) => setCfg({ weF: e.target.value === '' ? null : +e.target.value })} />× </label>}
      <small className="muted">{t.dprofLegend} · {t.dprofNight}: {nf(Math.min(...cur.slice(0, 6)))}× ({String(cur.indexOf(Math.min(...cur.slice(0, 6)))).padStart(2, '0')}:00)</small>
      <div className="row">
        <button className="btn btn-outline btn-sm" data-testid="pf-import" onClick={() => fr.current.click()}><Upload size={14} /> {t.dprofImport}</button>
        <input ref={fr} type="file" hidden accept=".csv,.txt,.xlsx,.xls,.ods" data-testid="pf-file" onChange={(e) => imp(e.target.files[0])} />
        <button className="btn btn-outline btn-sm" data-testid="pf-edit" onClick={open}><Pencil size={14} /> {t.dprofEdit}</button>
        {own && <button className="btn btn-outline btn-sm" data-testid="pf-reset" onClick={() => setCfg({ [K]: null, [SK]: null, ...(we ? { weF: null } : {}) })}><RotateCcw size={14} /> {t.dprofReset}</button>}
      </div>
      {edit && (
        <div className="dprof-edit" data-testid="pf-editor">
          <small className="muted">{t.dprofEditHint}</small>
          <div className="dprof-grid">{vals.map((v, h) => <label key={h}><span>{String(h).padStart(2, '0')}</span><input inputMode="decimal" data-testid={`pf-h-${h}`} value={v} onChange={(e) => setVals(vals.map((x, i) => (i === h ? e.target.value : x)))} /></label>)}</div>
          <div className="row"><button className="btn btn-accent btn-sm" data-testid="pf-save" onClick={save}><Check size={14} /> {t.dprofSave}</button><button className="btn btn-outline btn-sm" data-testid="pf-cancel" onClick={() => setEdit(false)}>{t.btnCancel}</button></div>
        </div>
      )}
    </div>
  );
}
